'use client'
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  useDisclosure,
  UseDisclosureProps,
} from '@core/ui-elements'
import { ForwardedRef, forwardRef, ReactNode, useImperativeHandle } from 'react'

export interface IFullBottomDrawer {
  header?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  color?: 'red' | 'default' | 'green' | 'yellow'
  canClose?: boolean
  defaultIsOpen?: boolean
  handleOnClose?: () => void
}

const FullBottomDrawer = forwardRef(
  (
    {
      header,
      children,
      footer,
      color = 'default',
      canClose = true,
      defaultIsOpen = false,
      handleOnClose: onClosePropFunc = () => null,
    }: IFullBottomDrawer,
    ref: ForwardedRef<UseDisclosureProps | undefined>
  ) => {
    const { isOpen, onOpen, onClose } = useDisclosure()

    useImperativeHandle(ref, () => ({
      onOpen,
      onClose,
    }))

    const colorScheme = {
      default: {
        backgroundColor: 'white',
        borderBottomColor: 'gray.200',
      },
      red: {
        backgroundColor: 'red.100',
        borderBottomColor: 'red.500',
      },
      green: {
        backgroundColor: 'green.100',
        borderBottomColor: 'green.500',
      },
      yellow: {
        backgroundColor: 'orange.100',
        borderBottomColor: 'yellow.500',
      },
    }

    const handleOnClose = () => {
      if (canClose) {
        onClosePropFunc()

        onClose()
      }
    }

    return (
      <Drawer
        autoFocus={true}
        closeOnEsc={canClose}
        closeOnOverlayClick={canClose}
        isOpen={defaultIsOpen ? defaultIsOpen : isOpen}
        placement={'bottom'}
        returnFocusOnClose={true}
        size={'full'}
        trapFocus={true}
        variant={'fullBottom'}
        onClose={handleOnClose}
      >
        <DrawerOverlay />
        <DrawerContent>
          {header ? (
            <DrawerHeader {...colorScheme[color]}>
              <Flex>{header}</Flex>
              {canClose ? <DrawerCloseButton size="md" /> : null}
            </DrawerHeader>
          ) : null}
          <DrawerBody>{children}</DrawerBody>
          {footer ? <DrawerFooter>{footer}</DrawerFooter> : null}
        </DrawerContent>
      </Drawer>
    )
  }
)

FullBottomDrawer.displayName = 'FullBottomDrawer'

export default FullBottomDrawer
